import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Banknote, RefreshCw, AlertCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const LABEL = {
  trainer_center: 'Trainer Center',
  olin: 'Olin',
};

// What each owner is owed, and the button that moves it. Trainer Center's share
// is listed but never transferred — it is already sitting in their account.
export default function AdminPayoutsPage() {
  const [owners, setOwners] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.functions.invoke('shinyvault-payouts', {
      body: { action: 'admin_summary' },
    });
    if (err || data?.error) { setError(data?.error || err.message); return; }
    setError('');
    setOwners(data.owners || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (owner) => {
    setBusy(owner); setNote('');
    const { data, error: err } = await supabase.functions.invoke('shinyvault-payouts', {
      body: { action: 'run_payout', owner },
    });
    setBusy('');
    if (err || data?.error) { setError(data?.error || err.message); return; }
    setError('');
    setNote(data.transferred_cents
      ? `Sent ${money(data.transferred_cents)} to ${LABEL[owner] || owner}.`
      : 'Nothing was owed.');
    load();
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <Banknote size={20} />
        <h1 style={{ fontSize: '1.4rem', fontWeight: 900, margin: 0 }}>Payouts</h1>
      </div>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 22px', lineHeight: 1.6 }}>
        Profit on each sale is what it sold for minus what it was bought for. App Catalyst takes 25
        percent automatically at the time of the sale. The remaining 75 percent is what shows here.
      </p>

      {error && (
        <div className="sv-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 10 }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{error}</div>
        </div>
      )}
      {note && (
        <div className="sv-card" style={{ padding: 16, marginBottom: 16 }}>{note}</div>
      )}

      {!owners ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : owners.length === 0 ? (
        <div className="sv-card" style={{ padding: 22, color: 'var(--text-muted)' }}>
          Nothing is owed right now.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {owners.map((o) => (
            <div key={o.owner} className="sv-card"
              style={{ padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{LABEL[o.owner] || o.owner}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.9rem', marginTop: 2 }}>
                  {o.lines} item{o.lines === 1 ? '' : 's'} waiting
                </div>
                {/* A refund on an already-paid item normally pulls their share
                    back out of their Stripe account. When that fails there is
                    nothing automatic left to try, and Trainer Center is quietly
                    covering the difference — so it says so, in words. */}
                {o.unrecovered_cents > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 8, color: 'var(--danger)', fontSize: '.85rem', maxWidth: 460, lineHeight: 1.5 }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      <strong>{money(o.unrecovered_cents)}</strong> was paid out on orders that
                      were later refunded and could not be pulled back. Trainer Center is
                      covering that until you settle it directly.
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 900 }}>{money(o.accrued_cents)}</div>
                {o.owner === 'trainer_center' ? (
                  <span style={{ color: 'var(--text-muted)', fontSize: '.88rem' }}>Already in the account</span>
                ) : (
                  <button className="sv-btn" onClick={() => run(o.owner)} disabled={busy === o.owner}>
                    {busy === o.owner ? 'Sending…' : 'Pay out'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <button className="sv-btn-outline" onClick={load}
        style={{ marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <RefreshCw size={15} /> Refresh
      </button>
    </div>
  );
}
